/**
 * AME Scooter Configurator — guided quiz for mobility scooter selection
 * ES5 IIFE, no dependencies, deferred loading
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ame-configurator]');
  if (!root) return;

  // --- DOM refs ---
  var ctaBtn = root.querySelector('[data-cfg-cta]');
  var wizard = root.querySelector('#ame-cfg-wizard');
  var resultsPanel = root.querySelector('[data-cfg-results]');
  var resultsGrid = root.querySelector('[data-cfg-results-grid]');
  var resultsHeading = root.querySelector('[data-cfg-results-heading]');
  var resultsSubtitle = root.querySelector('[data-cfg-results-subtitle]');
  var viewAllLink = root.querySelector('[data-cfg-view-all]');
  var progressLabel = root.querySelector('[data-cfg-progress-label]');
  var progressBar = root.querySelector('[data-cfg-progressbar]');
  var progressFill = root.querySelector('[data-cfg-progress-fill]');
  var liveRegion = root.querySelector('[data-cfg-live]');

  var steps = root.querySelectorAll('[data-cfg-step]');
  var TOTAL_STEPS = steps.length;

  // --- State ---
  var currentStep = 1;
  var answers = {};
  var products = [];

  // --- Parse product data ---
  var dataEl = document.getElementById('ame-configurator-data');
  if (dataEl) {
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      products = [];
    }
  }

  // --- Session storage helpers ---
  function saveState() {
    try {
      sessionStorage.setItem('ame-cfg-state', JSON.stringify({
        step: currentStep,
        answers: answers
      }));
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem('ame-cfg-state');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearState() {
    try { sessionStorage.removeItem('ame-cfg-state'); } catch (e) { /* ignore */ }
  }

  // --- Announce to screen readers ---
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  // --- Progress bar ---
  function updateProgress(step) {
    var pct = Math.round((step / TOTAL_STEPS) * 100);
    if (progressLabel) progressLabel.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }
  }

  // --- Show/hide steps ---
  function showStep(n) {
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.toggle('is-active', i === n - 1);
    }
    currentStep = n;
    updateProgress(n);

    // Restore any previously selected answer for this step
    var stepEl = steps[n - 1];
    var radios = stepEl.querySelectorAll('input[type="radio"]');
    var fieldName = radios.length > 0 ? radios[0].name : '';
    if (fieldName && answers[fieldName]) {
      for (var j = 0; j < radios.length; j++) {
        radios[j].checked = radios[j].value === answers[fieldName];
      }
    }

    // Update next button state
    updateNextBtn(stepEl);

    // Focus management
    var question = stepEl.querySelector('.ame-configurator__question');
    if (question) question.focus();

    announce('Step ' + n + ' of ' + TOTAL_STEPS);
    saveState();
  }

  // --- Next button enabled/disabled ---
  function updateNextBtn(stepEl) {
    var nextBtn = stepEl.querySelector('[data-cfg-next]');
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (nextBtn) nextBtn.disabled = !checked;
  }

  // --- Open wizard ---
  function openWizard() {
    wizard.hidden = false;
    ctaBtn.setAttribute('aria-expanded', 'true');
    showStep(currentStep);
    // Scroll the wizard panel into view after a frame so layout settles
    requestAnimationFrame(function () {
      var rect = wizard.getBoundingClientRect();
      var headerEl = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      // Scroll so the wizard top sits just below the sticky header
      window.scrollTo({ top: window.scrollY + rect.top - headerH - 16, behavior: 'smooth' });
    });
  }

  // --- Close wizard ---
  function closeWizard() {
    wizard.hidden = true;
    resultsPanel.classList.remove('is-active');
    ctaBtn.setAttribute('aria-expanded', 'false');
    ctaBtn.focus();
    currentStep = 1;
    answers = {};
    clearState();
  }

  // --- Collect answer from current step ---
  function collectAnswer() {
    var stepEl = steps[currentStep - 1];
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (checked) {
      answers[checked.name] = checked.value;
    }
  }

  // --- Tag helper ---
  function hasTag(tags, tag) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === tag) return true;
    }
    return false;
  }

  // --- Scoring algorithm ---
  function scoreUseCase(product, useCase) {
    var tags = product.tags || [];
    var score = 0;
    switch (useCase) {
      case 'indoor':
        if (hasTag(tags, 'indoor')) score += 8;
        if (hasTag(tags, 'compact')) score += 4;
        if (hasTag(tags, '3-wheel')) score += 3;
        if (product.turning_radius !== null && product.turning_radius < 45) score += 4;
        if (product.product_weight !== null && product.product_weight < 100) score += 3;
        if (!hasTag(tags, 'indoor') && hasTag(tags, 'outdoor')) score -= 3;
        break;
      case 'outdoor':
        if (hasTag(tags, 'outdoor')) score += 8;
        if (hasTag(tags, '4-wheel')) score += 4;
        if (hasTag(tags, 'long-range')) score += 4;
        if (product.range_miles !== null && product.range_miles > 15) score += 4;
        if (product.weight_capacity !== null && product.weight_capacity > 300) score += 3;
        if (!hasTag(tags, 'outdoor') && !hasTag(tags, 'all-terrain')) score -= 3;
        break;
      case 'terrain':
        if (hasTag(tags, 'all-terrain')) score += 12;
        if (hasTag(tags, 'rugged')) score += 4;
        if (hasTag(tags, 'heavy-duty')) score += 4;
        if (hasTag(tags, '4-wheel')) score += 3;
        if (product.weight_capacity !== null && product.weight_capacity >= 400) score += 3;
        if (product.range_miles !== null && product.range_miles > 20) score += 3;
        if (!hasTag(tags, 'all-terrain') && !hasTag(tags, 'outdoor')) score -= 5;
        break;
      case 'travel':
        if (hasTag(tags, 'travel')) score += 8;
        if (hasTag(tags, 'folding')) score += 5;
        if (hasTag(tags, 'lightweight')) score += 3;
        if (hasTag(tags, 'portable')) score += 3;
        if (hasTag(tags, 'tsa-approved') || hasTag(tags, 'airline-approved')) score += 3;
        if (product.disassembles) score += 3;
        if (product.product_weight !== null && product.product_weight < 80) score += 4;
        if (product.heaviest_piece_lbs !== null && product.heaviest_piece_lbs < 35) score += 3;
        if (!hasTag(tags, 'travel') && !hasTag(tags, 'portable') && !hasTag(tags, 'folding')) score -= 5;
        break;
    }
    return score;
  }

  function scoreCapacity(product, minCapacity) {
    if (product.weight_capacity === null) return 0;
    if (product.weight_capacity < minCapacity) return -100;
    return 10;
  }

  function scorePortability(product, portability) {
    var tags = product.tags || [];
    var score = 0;
    switch (portability) {
      case 'very':
        if (hasTag(tags, 'portable')) score += 5;
        if (hasTag(tags, 'folding')) score += 5;
        if (hasTag(tags, 'lightweight')) score += 4;
        if (hasTag(tags, 'compact')) score += 3;
        if (product.disassembles) score += 3;
        if (product.product_weight !== null && product.product_weight < 80) score += 5;
        if (product.heaviest_piece_lbs !== null && product.heaviest_piece_lbs < 30) score += 3;
        if (hasTag(tags, 'full-size') || hasTag(tags, 'heavy-duty')) score -= 5;
        break;
      case 'somewhat':
        if (hasTag(tags, 'portable')) score += 3;
        if (hasTag(tags, 'compact')) score += 3;
        if (product.disassembles) score += 4;
        if (product.product_weight !== null && product.product_weight < 120) score += 3;
        break;
      case 'not_important':
        if (hasTag(tags, 'full-size')) score += 5;
        if (hasTag(tags, 'heavy-duty')) score += 3;
        if (product.product_weight !== null && product.product_weight > 150) score += 3;
        if (hasTag(tags, 'portable') && hasTag(tags, 'lightweight')) score -= 3;
        break;
    }
    return score;
  }

  function scoreBudget(product, budget) {
    if (product.call_for_price) return 5;

    // Price is in cents
    var priceDollars = product.price / 100;

    if (budget === 'flexible') return 10;

    var budgetNum = parseInt(budget, 10);
    var lo = 0;
    var hi = budgetNum;

    switch (budget) {
      case '800':
        lo = 0; hi = 800;
        break;
      case '1500':
        lo = 800; hi = 1500;
        break;
      case '2500':
        lo = 1500; hi = 2500;
        break;
      case '9999':
        lo = 2500; hi = 999999;
        break;
    }

    if (priceDollars >= lo && priceDollars <= hi) return 10;
    if (priceDollars <= hi * 1.2) return 5;
    return 0;
  }

  function scoreProducts(ans) {
    var useCase = ans.use_case || 'outdoor';
    var minCapacity = parseInt(ans.weight, 10) || 250;
    var portability = ans.portability || 'somewhat';
    var budget = ans.budget || 'flexible';

    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];

      // Skip non-scooter products (vehicle lifts, ramps, accessories, wheelchairs)
      if (!hasTag(p.tags, 'mobility-scooter')) continue;

      var uScore = scoreUseCase(p, useCase);
      var cScore = scoreCapacity(p, minCapacity);
      var pScore = scorePortability(p, portability);
      var bScore = scoreBudget(p, budget);

      var total = (uScore * 3) + (cScore * 2) + (pScore * 2) + (bScore * 1);

      scored.push({
        product: p,
        total: total,
        useScore: uScore,
        capacityScore: cScore,
        portabilityScore: pScore,
        budgetScore: bScore
      });
    }

    scored.sort(function (a, b) { return b.total - a.total; });
    return scored;
  }

  // --- Match badge label ---
  function getMatchLabel(index) {
    switch (index) {
      case 0: return 'Best Match';
      case 1: return 'Great Fit';
      case 2: return 'Strong Pick';
      default: return 'Good Option';
    }
  }

  // --- Format price ---
  function formatPrice(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Build spec pills ---
  function buildSpecs(product) {
    var specs = [];
    if (product.weight_capacity) specs.push(product.weight_capacity + ' lb capacity');
    if (product.range_miles) specs.push(product.range_miles + ' mi range');
    if (product.max_speed) specs.push(product.max_speed + ' mph');
    if (product.product_weight) specs.push(product.product_weight + ' lbs');
    if (product.disassembles) specs.push('Disassembles');
    return specs.slice(0, 3);
  }

  // --- Render result cards ---
  function renderResults(scored) {
    var top = [];
    for (var i = 0; i < scored.length && top.length < 5; i++) {
      // Only show products with positive total scores (good matches)
      if (scored[i].total > 0) top.push(scored[i]);
    }

    // Fallback: if no products above threshold, show top 3 closest matches
    if (top.length === 0 && scored.length > 0) {
      for (var f = 0; f < scored.length && top.length < 3; f++) {
        top.push(scored[f]);
      }
    }

    resultsGrid.innerHTML = '';

    if (top.length === 0) {
      resultsGrid.innerHTML =
        '<div class="ame-configurator__no-match">' +
          '<p>No exact matches found for your criteria.</p>' +
          '<p>Try adjusting your preferences or <a href="tel:+18039261493">call (803) 926-1493</a> for expert help.</p>' +
        '</div>';
      resultsSubtitle.textContent = '';
      announce('No exact matches found. Try adjusting your preferences or call for help.');
      return top;
    }

    // Adjust messaging if using fallback (all scores <= 0)
    if (top[0].total <= 0) {
      resultsSubtitle.textContent =
        'We couldn\'t find an exact match, but here are our closest options. ' +
        'Call (803) 926-1493 for personalized help.';
    } else {
      resultsSubtitle.textContent = 'Based on your answers, here are our top ' + top.length + ' recommendations.';
    }

    for (var j = 0; j < top.length; j++) {
      var p = top[j].product;
      var specs = buildSpecs(p);
      var specsHtml = '';
      for (var k = 0; k < specs.length; k++) {
        specsHtml += '<span class="ame-configurator__card-spec">' + escapeHtml(specs[k]) + '</span>';
      }

      var priceHtml = '';
      if (p.call_for_price) {
        priceHtml = '<span style="color:var(--ame-primary)">Call for Price</span>';
      } else {
        priceHtml = formatPrice(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <span class="ame-card__compare-price">' + formatPrice(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor ? '<span class="ame-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '';

      var card =
        '<a href="' + escapeHtml(p.url) + '" class="ame-card">' +
          '<div class="ame-card__image-wrap">' +
            '<span class="ame-configurator__match-badge">' + getMatchLabel(j) + '</span>' +
            (p.image ? '<img class="ame-card__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="500" height="500">' : '') +
          '</div>' +
          '<div class="ame-card__body">' +
            vendorHtml +
            '<h3 class="ame-card__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="ame-configurator__card-specs">' + specsHtml + '</div>' +
            '<div class="ame-card__price">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';

      resultsGrid.insertAdjacentHTML('beforeend', card);
    }

    announce('Found ' + top.length + ' matching scooters. Showing your top recommendations.');
    return top;
  }

  // --- Build filter URL for "View All" ---
  function buildFilterUrl(ans) {
    var base = '/collections/mobility-scooters';
    var params = [];

    // Use case → tag filter
    if (ans.use_case) {
      switch (ans.use_case) {
        case 'indoor':
          params.push('filter.p.tag=indoor');
          break;
        case 'outdoor':
          params.push('filter.p.tag=outdoor');
          break;
        case 'terrain':
          params.push('filter.p.tag=all-terrain');
          break;
        case 'travel':
          params.push('filter.p.tag=travel');
          break;
      }
    }

    // Budget → price filter (Shopify storefront uses dollars)
    if (ans.budget && ans.budget !== 'flexible') {
      switch (ans.budget) {
        case '800':
          params.push('filter.v.price.lte=800');
          break;
        case '1500':
          params.push('filter.v.price.gte=800');
          params.push('filter.v.price.lte=1500');
          break;
        case '2500':
          params.push('filter.v.price.gte=1500');
          params.push('filter.v.price.lte=2500');
          break;
        case '9999':
          params.push('filter.v.price.gte=2500');
          break;
      }
    }

    return params.length > 0 ? base + '?' + params.join('&') : base;
  }

  // --- HTML escaping ---
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // --- Show results ---
  function showResults() {
    wizard.hidden = true;
    var scored = scoreProducts(answers);
    var top = renderResults(scored);

    // Update view all link
    if (viewAllLink) viewAllLink.href = buildFilterUrl(answers);

    resultsPanel.classList.add('is-active');
    if (resultsHeading) resultsHeading.focus();

    clearState();
  }

  // --- Event: CTA click ---
  ctaBtn.addEventListener('click', function () {
    if (wizard.hidden && !resultsPanel.classList.contains('is-active')) {
      openWizard();
    } else {
      closeWizard();
    }
  });

  // --- Event: radio change → enable next ---
  root.addEventListener('change', function (e) {
    if (e.target.type === 'radio' && e.target.closest('[data-cfg-step]')) {
      var stepEl = e.target.closest('[data-cfg-step]');
      updateNextBtn(stepEl);
    }
  });

  // --- Event: next ---
  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-cfg-next]');
    if (!nextBtn || nextBtn.disabled) return;

    collectAnswer();

    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      showResults();
    }
  });

  // --- Event: back ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cfg-back]')) {
      collectAnswer();
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    }
  });

  // --- Event: close ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cfg-close]')) {
      closeWizard();
    }
  });

  // --- Event: restart ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cfg-restart]')) {
      resultsPanel.classList.remove('is-active');
      answers = {};
      currentStep = 1;

      // Clear all radios
      var radios = root.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = false;
      }

      clearState();
      openWizard();
    }
  });

  // --- Event: Banner CTA (button in ame-collection-banner) ---
  var bannerCta = document.querySelector('[data-cfg-banner-cta]');
  if (bannerCta) {
    bannerCta.addEventListener('click', function () {
      openWizard();
    });
  }

  // --- Event: Escape key ---
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !wizard.hidden) {
      closeWizard();
    }
  });

  // --- Restore session ---
  var saved = loadState();
  if (saved && saved.step && saved.answers) {
    answers = saved.answers;
    currentStep = saved.step;

    // Restore radio selections
    var fieldNames = Object.keys(answers);
    for (var f = 0; f < fieldNames.length; f++) {
      var radio = root.querySelector('input[name="' + fieldNames[f] + '"][value="' + answers[fieldNames[f]] + '"]');
      if (radio) radio.checked = true;
    }

    openWizard();
  }
})();
